import React, { useEffect, useState } from "react";
import { notification } from "antd";
import GroceryDelivery from "../../components/Grocery/Delivery/GroceryDelivery";
import {
  CategorizedGroceryItems,
  GroceryCategory,
  GroceryItem,
} from "../../types/GroceryTypes";
import {
  getGroceryCategoryByOrganizationId,
  getGroceryDashboard,
  getTemporaryGroceryCart,
} from "../../services/GroceryService";
import { useTripsCtx } from "../../context/TripDetailsContext";
import { useGroceryCtx } from "../../context/GroceryContext";
import { useReloadServiceUsers } from "../../hooks/useReloadServiceUsers";
import {
  batchFetchImageUrls,
  getServiceUserDetails,
} from "../../helpers/personHelpers";
import { useParams } from "react-router-dom";
import { getWandaServiceArea } from "../../services";

function GroceryDeliveryContainer() {
  const [groceryCategories, setGroceryCategories] = useState<GroceryCategory[]>(
    []
  );
  const [groceries, setGroceries] = useState<CategorizedGroceryItems[]>([]);
  const [isLoadingGroceries, setIsLoadingGroceries] = useState<boolean>(false);

  const { personId } = useParams<{ personId: string }>();

  const { isServiceUsersLoading, serviceDataCtx } = useTripsCtx();
  const [serviceData] = serviceDataCtx;

  const {
    selectedGroceryCategoryIdsCtx,
    deliveryAddressCtx,
    cachedGroceryCartItemsCtx,
    deliveryNoteCtx,
  } = useGroceryCtx();
  const [, setCachedGroceryCartItems] = cachedGroceryCartItemsCtx;
  const [selectedGroceryCategoryIds] = selectedGroceryCategoryIdsCtx;
  const [deliveryAddress, setDeliveryAddress] = deliveryAddressCtx;
  const [, setDeliveryNote] = deliveryNoteCtx;

  const { wandaOrgId } = useTripsCtx();

  useReloadServiceUsers();

  useEffect(() => {
    !isServiceUsersLoading &&
      !deliveryAddress?.organizationId &&
      (async () => {
        try {
          const tempGroceryCart = await getTemporaryGroceryCart(
            Number(personId)
          );
          setCachedGroceryCartItems(tempGroceryCart.getGroceryCarts || []);
          setDeliveryNote(tempGroceryCart.deliveryNote);

          if (tempGroceryCart.deliveryAddress) {
            setDeliveryAddress(() => ({
              ...tempGroceryCart.deliveryAddress.address,
              organizationId: tempGroceryCart.deliveryAddress.wandaMarketArea,
            }));
          } else {
            const homeAddress = getServiceUserDetails(
              Number(personId),
              serviceData
            )?.personAttributes.homeAddress;
            const serviceAreas = await getWandaServiceArea(homeAddress?.zip);

            setDeliveryAddress(() => ({
              ...homeAddress,
              organizationId: serviceAreas[0]?.organizationId,
            }));
          }
        } catch (error) {
          notification.error({
            message: "Failed to fetch service area",
          });
        }
      })();
  }, [isServiceUsersLoading]);

  useEffect(() => {
    getGroceries();
  }, [
    wandaOrgId.privatePayId,
    selectedGroceryCategoryIds,
    isServiceUsersLoading,
    deliveryAddress?.zip,
  ]);

  async function getGroceries() {
    setIsLoadingGroceries(true);
    if (
      wandaOrgId.privatePayId &&
      !isServiceUsersLoading &&
      deliveryAddress?.organizationId
    ) {
      try {
        const groceryCategoriesApiResult = await getGroceryCategoryByOrganizationId(
          wandaOrgId.privatePayId,
          true
        );
        const allGroceryCategoryIds = groceryCategoriesApiResult.map(
          (category) => category.id
        );

        setGroceryCategories(groceryCategoriesApiResult);

        const { organizationId, fullAddress, ...rest } = deliveryAddress;

        const groceryItemsApiResult = await getGroceryDashboard({
          organizationId,
          groceryCategoryIds:
            selectedGroceryCategoryIds || allGroceryCategoryIds,
          serviceUserPersonId: Number(personId),
          address: rest,
          // Filters TBD - 06.02
          productName: undefined,
          brand: undefined,
          upcCode: undefined,
          // Pagination TBD - 06.02
          pageNumber: 1,
          pageSize: 1000,
        });

        const imageKeys = groceryItemsApiResult.reduce(
          (acc: string[], groceryItem) => {
            if (groceryItem.imageKey) {
              acc.push(groceryItem.imageKey);
            }
            return acc;
          },
          []
        );

        let imageUrlsMap: Map<string, string>;
        if (imageKeys.length) {
          imageUrlsMap = await batchFetchImageUrls({
            folderName: "ccp",
            imageKeys,
          });
        }

        const categorizedGroceries = Object.values(
          groceryItemsApiResult.reduce(
            (
              acc: Record<string, CategorizedGroceryItems>,
              groceryItem: GroceryItem
            ) => {
              const { categoryName } = groceryItem;
              if (!acc[categoryName]) {
                acc[categoryName] = {
                  categoryName,
                  groceryItems: [],
                };
              }
              acc[categoryName].groceryItems.push({
                ...groceryItem,
                imageUrl:
                  groceryItem.imageKey &&
                  imageUrlsMap?.get(groceryItem.imageKey),
              });
              return acc;
            },
            {} as Record<string, CategorizedGroceryItems>
          )
        );

        setGroceries(categorizedGroceries);
      } catch (error) {
        notification.error({
          message: "Failed to Fetch Groceries",
        });
      } finally {
        setIsLoadingGroceries(false);
      }
    }
  }

  return (
    <GroceryDelivery
      isLoadingGroceries={isLoadingGroceries}
      groceryCategories={groceryCategories}
      groceries={groceries}
    />
  );
}

export default GroceryDeliveryContainer;
